import { useRef, useEffect, useState } from "react";
import { motion, useInView, useMotionValue, animate } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";
import { useLang } from "../i18n/LangContext";
import { Magnetic } from "./Magnetic";
import { EditorialNumber } from "./EditorialNumber";

const revealUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.9, ease: [0.7, 0, 0.2, 1] },
  }),
};

/**
 * AnimatedMetric — parses a KPI string ("+34%", "9.6/10", "0", "+18 pp", "100%")
 * and counts the primary numeric value from 0 to the target when in view.
 * Non-numeric parts (prefix, suffix) remain static.
 */
const AnimatedMetric = ({ k }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [display, setDisplay] = useState("");
  const mv = useMotionValue(0);

  // Split "+34%" → prefix="+" number=34 suffix="%"
  const match = k.match(/^([^\d.]*)([\d.]+)(.*)$/);
  const prefix = match ? match[1] : "";
  const target = match ? parseFloat(match[2]) : 0;
  const suffix = match ? match[3] : k;
  const decimals = match && match[2].includes(".") ? match[2].split(".")[1].length : 0;

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, target, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v.toFixed(decimals)),
    });
    return () => controls.stop();
  }, [inView, target, decimals, mv]);

  return (
    <span ref={ref}>
      {prefix}
      {display || (target === 0 ? "0" : "0")}
      {suffix}
    </span>
  );
};

export const CaseStudy = () => {
  const { t } = useLang();
  const cs = t.caseStudy;
  if (!cs) return null;

  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="case-study"
      className="relative bg-[#F1EEE8] text-[#232326] py-28 md:py-40 overflow-hidden border-t border-[#232326]/10"
      data-testid="case-study-section"
    >
      <EditorialNumber n="05" variant="dark" align="right" />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-8 mb-16 md:mb-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={revealUp}
            className="md:col-span-3"
          >
            <div className="flex items-center gap-3 text-[#13535A] text-[0.7rem] tracking-[0.28em] uppercase font-semibold font-medium">
              <span className="w-6 h-px bg-[#8A6F45]" />
              <span>{cs.overline}</span>
            </div>
          </motion.div>
          <div className="md:col-span-9">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={revealUp}
              custom={1}
              className="font-display font-medium leading-[1.02] tracking-tight text-[clamp(2.2rem,5.4vw,4.8rem)] whitespace-pre-line text-[#0A0908]"
            >
              {cs.title}
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={revealUp}
              custom={2}
              className="text-[#232326]/75 text-lg leading-relaxed max-w-2xl mt-6"
            >
              {cs.lead}
            </motion.p>
          </div>
        </div>

        {/* Documentation badge */}
        {cs.docBadge && (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
            variants={revealUp}
            className="inline-flex items-center gap-3 border border-[#8A6F45]/40 px-5 py-3 mb-12 bg-[#F1EEE8]/60 backdrop-blur-sm"
          >
            <span className="w-2 h-2 rounded-full bg-[#8A6F45] animate-pulse" />
            <span className="text-[0.68rem] tracking-[0.24em] uppercase text-[#0A0908] font-semibold">
              {cs.docBadge}
            </span>
          </motion.div>
        )}

        {/* Property card */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={revealUp}
          className="grid md:grid-cols-12 gap-6 md:gap-10 mb-16"
        >
          <div className="md:col-span-7 relative overflow-hidden clip-card">
            <div className="relative aspect-[16/10]">
              <img
                src={cs.property.cover}
                alt={cs.property.name}
                className="absolute inset-0 w-full h-full object-cover bg-ken-burns"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908]/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-[#F1EEE8]">
                <div className="text-[0.62rem] tracking-[0.32em] uppercase text-[#DDD4B9] mb-2">
                  {cs.property.typology}
                </div>
                <div className="font-display text-2xl md:text-3xl tracking-tight">
                  {cs.property.name}
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-5 flex flex-col justify-between gap-8">
            <div>
              <div className="text-[0.62rem] tracking-[0.32em] uppercase text-[#8A6F45] mb-3 font-semibold">
                {cs.challenge.label}
              </div>
              <h3 className="font-display text-2xl md:text-3xl leading-tight tracking-tight text-[#0A0908] mb-3">
                {cs.challenge.title}
              </h3>
              <p className="text-[#232326]/80 leading-relaxed text-[0.98rem]">
                {cs.challenge.body}
              </p>
            </div>
            <div>
              <div className="text-[0.62rem] tracking-[0.32em] uppercase text-[#13535A] mb-3 font-semibold">
                {cs.approach.label}
              </div>
              <h3 className="font-display text-2xl md:text-3xl leading-tight tracking-tight text-[#0A0908] mb-3">
                {cs.approach.title}
              </h3>
              <p className="text-[#232326]/80 leading-relaxed text-[0.98rem]">
                {cs.approach.body}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Metrics band */}
        {cs.metricsHeader && (
          <div className="mb-8 md:mb-10 flex items-baseline justify-between gap-4 flex-wrap">
            <div className="text-[0.68rem] tracking-[0.28em] uppercase text-[#8A6F45] font-semibold">
              {cs.metricsHeader}
            </div>
            <div className="text-[0.65rem] tracking-[0.22em] uppercase text-[#232326]/50 font-medium">
              Fonte · Rendiconto Zitro Q4
            </div>
          </div>
        )}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 border-t-2 border-b-2 border-[#232326]/20 py-12 md:py-16">
          {cs.metrics.map((m, i) => (
            <motion.div
              key={m.label}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-40px" }}
              variants={revealUp}
              custom={i}
              className="text-center md:text-left"
              data-testid={`case-metric-${i}`}
            >
              <div
                className="font-display leading-none text-[#0A0908] tracking-tight tabular-nums"
                style={{ fontSize: "clamp(2.6rem, 5vw, 4.4rem)", fontWeight: 500 }}
              >
                <AnimatedMetric k={m.k} />
                <span className="text-[#8A6F45]">.</span>
              </div>
              <div className="mt-3 font-medium tracking-[0.06em] text-[#0A0908] text-base md:text-lg">
                {m.label}
              </div>
              <div className="mt-1 text-[#232326]/60 text-xs md:text-sm leading-relaxed">{m.note}</div>
            </motion.div>
          ))}
        </div>

        {/* Quote + CTA */}
        <div className="mt-16 md:mt-20 grid md:grid-cols-12 gap-8">
          <motion.blockquote
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
            variants={revealUp}
            className="md:col-span-8 italic-serif text-2xl md:text-3xl leading-[1.35] text-[#0A0908]"
          >
            &ldquo;{cs.quote}&rdquo;
            <div className="mt-6 not-italic text-sm tracking-[0.18em] uppercase text-[#232326]/60 font-medium font-sans">
              {cs.quoteAuthor}
            </div>
          </motion.blockquote>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
            variants={revealUp}
            custom={1}
            className="md:col-span-4 flex md:items-end md:justify-end"
          >
            <Magnetic strength={0.2}>
              <button
                onClick={scrollToContact}
                className="btn-primary"
                data-testid="case-study-cta"
              >
                {cs.cta}
                <ArrowUpRight size={16} weight="bold" />
              </button>
            </Magnetic>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
